import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import { DEFAULT_QUANTITY_UNITS, formatQuantityWithUnit, resolveDisplayUnit } from '../../../components/quantityUnit';
import { formatVendorDeviceLabel, readVendorDeviceMeta } from '../../../components/resourceConfig';
import { formatVendorDeviceCellLabel, hasVendorDeviceSelection } from '../../../components/vendorAndDeviceRadioList';
import { hasMeaningfulValue } from '../../../utils';
import { isVendorQuantityOptional } from '../../../utils/postPaidPricing';
import type { ComputeModalResourceKind, ComputePaths } from '../types';
import { isCpuConfigured, isGpuConfigured, isMemoryConfigured, readCpuTotal } from './cpu';
import { resolveVendorDevicePath } from './paths';

export interface ComputeSummaryItem {
  label: string;
  value: string;
}

/** getFieldsValue 可能漏未挂载叶子，与 store / getFieldValue 合并 */
function readComputeFormStore(form: FormInstance): Record<string, unknown> {
  const nested = (form.getFieldsValue() as Record<string, unknown>) || {};
  const store = (form.getFields() as Record<string, unknown>) || {};
  return { ...store, ...nested };
}

function readComputeFormPath(form: FormInstance, path: string): unknown {
  const fromField = form.getFieldValue(path);
  if (fromField !== undefined && fromField !== null && fromField !== '') return fromField;
  return get(readComputeFormStore(form), path);
}

function readPricingQuantityUnit(
  formValues: Record<string, unknown>,
  paths: ComputePaths,
  kind: ComputeModalResourceKind,
): string | undefined {
  return readVendorDeviceMeta(formValues, resolveVendorDevicePath(kind, paths))?.quantityUnit;
}

export function isComputeResourceConfigured(
  kind: ComputeModalResourceKind,
  formValues: Record<string, unknown>,
  paths: ComputePaths,
  billModelId?: string,
  paymentTiming?: string,
  skuType?: string,
  resourceEditMode?: string,
): boolean {
  const vendorPath = resolveVendorDevicePath(kind, paths);
  const hasVendor =
    hasVendorDeviceSelection(readVendorDeviceMeta(formValues, vendorPath)) ||
    (kind === 'cpu' &&
      vendorPath.endsWith('cpuPricing') &&
      hasVendorDeviceSelection(readVendorDeviceMeta(formValues, vendorPath.replace(/cpuPricing$/, 'cpu.pricing'))));

  /** 后付费 / 自定义规格创建：已选厂商设备即可，数量/规格非必填；GPU 仍要求数量 */
  if (isVendorQuantityOptional({ billModelId, paymentTiming, skuType, resourceEditMode }) && hasVendor) {
    if (kind === 'gpu') {
      return isGpuConfigured(get(formValues, paths.gpu));
    }
    return true;
  }

  const baseConfigured =
    kind === 'cpu'
      ? isCpuConfigured(formValues, paths)
      : kind === 'memory'
        ? isMemoryConfigured(formValues, paths)
        : isGpuConfigured(get(formValues, paths.gpu));

  if (!baseConfigured) return false;
  if (!billModelId) return true;
  return hasVendor;
}

/** 是否已有算力资源数据（规格或厂商任一即可，用于展示卡片） */
export function hasComputeResourceFormData(
  kind: ComputeModalResourceKind,
  formValues: Record<string, unknown>,
  paths: ComputePaths,
): boolean {
  const vendorPath = resolveVendorDevicePath(kind, paths);
  const hasVendor =
    hasVendorDeviceSelection(readVendorDeviceMeta(formValues, vendorPath)) ||
    (kind === 'cpu' &&
      vendorPath.endsWith('cpuPricing') &&
      hasVendorDeviceSelection(readVendorDeviceMeta(formValues, vendorPath.replace(/cpuPricing$/, 'cpu.pricing'))));
  const baseConfigured =
    kind === 'cpu'
      ? isCpuConfigured(formValues, paths)
      : kind === 'memory'
        ? isMemoryConfigured(formValues, paths)
        : isGpuConfigured(get(formValues, paths.gpu));
  return baseConfigured || hasVendor;
}

export function buildCpuSummary(form: FormInstance, paths: ComputePaths, coreLabel: string, pricing?: boolean): string {
  const formValues = readComputeFormStore(form);
  if (paths.cpuRequest) {
    const leaf = readComputeFormPath(form, paths.cpuRequest);
    if (hasMeaningfulValue(leaf)) formValues[paths.cpuRequest] = leaf;
  }
  if (!isCpuConfigured(formValues, paths)) return '-';
  const unit = resolveDisplayUnit({
    quantityUnit: readPricingQuantityUnit(formValues, paths, 'cpu'),
    pricing,
    defaultUnit: DEFAULT_QUANTITY_UNITS.cpu,
    directLabel: coreLabel,
  });
  return formatQuantityWithUnit(readCpuTotal(formValues, paths), unit);
}

export function buildMemorySummary(form: FormInstance, paths: ComputePaths, pricing?: boolean): string {
  const formValues = readComputeFormStore(form);
  const memory = readComputeFormPath(form, paths.memory) ?? get(formValues, paths.memory);
  if (!hasMeaningfulValue(memory)) return '-';
  const unit = resolveDisplayUnit({
    quantityUnit: readPricingQuantityUnit(formValues, paths, 'memory'),
    pricing,
    defaultUnit: DEFAULT_QUANTITY_UNITS.memory,
    lockDisplayToDefault: true,
  });
  return formatQuantityWithUnit(memory, unit);
}

export function buildGpuSummary(form: FormInstance, paths: ComputePaths, pricing?: boolean): string {
  const formValues = readComputeFormStore(form);
  const gpu = (readComputeFormPath(form, paths.gpu) ?? get(formValues, paths.gpu)) as Record<string, unknown> | undefined;
  if (!isGpuConfigured(gpu)) return '-';
  const vendor = gpu?.vendor ? formatVendorDeviceCellLabel(String(gpu.vendor)) : undefined;
  const device = gpu?.device ? formatVendorDeviceCellLabel(String(gpu.device)) : undefined;
  const label = [gpu?.name, vendor, device].filter(Boolean).join(' ');
  const qty = gpu?.number ?? readComputeFormPath(form, `${paths.gpu}.number`);
  const unit = resolveDisplayUnit({
    quantityUnit: readPricingQuantityUnit(formValues, paths, 'gpu'),
    pricing,
    defaultUnit: DEFAULT_QUANTITY_UNITS.gpu,
    omitWhenDirect: true,
  });
  const qtyText = formatQuantityWithUnit(qty, unit, String(qty ?? '-'));
  return label ? `${label} × ${qtyText}` : qtyText;
}

function buildVendorDeviceSummary(form: FormInstance, paths: ComputePaths, kind: ComputeModalResourceKind): string {
  return formatVendorDeviceLabel(readVendorDeviceMeta(readComputeFormStore(form), resolveVendorDevicePath(kind, paths)));
}

export function buildCpuSummaryItems(form: FormInstance, paths: ComputePaths, coreLabel: string, pricing?: boolean): ComputeSummaryItem[] {
  return [
    { label: '资源配置', value: buildCpuSummary(form, paths, coreLabel, pricing) },
    { label: '厂商设备', value: buildVendorDeviceSummary(form, paths, 'cpu') },
  ];
}

export function buildMemorySummaryItems(form: FormInstance, paths: ComputePaths, pricing?: boolean): ComputeSummaryItem[] {
  return [
    { label: '资源配置', value: buildMemorySummary(form, paths, pricing) },
    { label: '厂商设备', value: buildVendorDeviceSummary(form, paths, 'memory') },
  ];
}

export function buildGpuSummaryItems(form: FormInstance, paths: ComputePaths, pricing?: boolean): ComputeSummaryItem[] {
  return [
    { label: '资源配置', value: buildGpuSummary(form, paths, pricing) },
    { label: '厂商设备', value: buildVendorDeviceSummary(form, paths, 'gpu') },
  ];
}
